import {PullRequestStateIcons} from '@github-ui/issue-pr-state/Icons'
import type {PullRequestPickerPullRequest} from '@github-ui/item-picker/PullRequestAndBranchPicker'
import {getPrState} from '@github-ui/item-picker/PullRequestAndBranchPicker'
import {TagIcon} from '@primer/octicons-react'
import {ActionList, Label} from '@primer/react'
import React, {useCallback} from 'react'
import {graphql} from 'react-relay'

import type {PullRequestRelease} from './DevelopmentSection'
import styles from './LinkedPullRequests.module.css'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const LinkedPullRequestsReleaseFragment = graphql`
  fragment LinkedPullRequestsRelease on Release @inline {
    id
    tagName
    name
    url
    isPrerelease
    isLatest
  }
`

type LinkedPullRequestsProps = {
  linkedPullRequests: PullRequestPickerPullRequest[]
  releaseByPrId?: Map<string, PullRequestRelease>
}

export function LinkedPullRequests({linkedPullRequests, releaseByPrId}: LinkedPullRequestsProps) {
  return (
    <>
      {linkedPullRequests.map(pullRequest => {
        const release = releaseByPrId?.get(pullRequest.id)
        return (
          <ActionList.LinkItem
            href={pullRequest.url}
            target="_blank"
            key={pullRequest.id}
            className={styles.ActionList_LinkItem}
          >
            <ActionList.LeadingVisual>{getPrIcon(pullRequest)}</ActionList.LeadingVisual>
            {pullRequest.title}
            {(pullRequest.repository?.nameWithOwner || release) && (
              <ActionList.Description variant="block">
                {pullRequest.repository?.nameWithOwner}
                {release && <PullRequestReleaseRow release={release} />}
              </ActionList.Description>
            )}
          </ActionList.LinkItem>
        )
      })}
    </>
  )
}

const PullRequestReleaseRow = React.memo(function PullRequestReleaseRow({release}: {release: PullRequestRelease}) {
  const handleNavigate = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- standard role="link" activation, not a keyboard shortcut
      if ('key' in e && e.key !== 'Enter' && e.key !== ' ') return
      e.preventDefault()
      e.stopPropagation()
      window.open(release.url, '_blank', 'noopener,noreferrer')
    },
    [release.url],
  )

  return (
    <span
      role="link"
      tabIndex={0}
      aria-label={`Release ${release.name || release.tagName}`}
      className={styles.ReleaseRow}
      onClick={handleNavigate}
      onKeyDown={handleNavigate}
    >
      <TagIcon size={16} className={styles.ReleaseIcon} />
      <span className={styles.ReleaseTagName}>{release.tagName}</span>
      {release.isLatest && (
        <Label variant="success" size="small">
          Latest
        </Label>
      )}
      {release.isPrerelease && (
        <Label variant="attention" size="small">
          Pre-release
        </Label>
      )}
    </span>
  )
})

function getPrIcon(pullRequest: PullRequestPickerPullRequest) {
  const prState = getPrState(
    pullRequest.isDraft && pullRequest.state === 'OPEN',
    pullRequest.isInMergeQueue,
    pullRequest.state,
  )
  const Icon = PullRequestStateIcons[prState]
  return <Icon />
}
